import { FC, useEffect, useRef } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { IconLanguage, IconPrinter, IconShare2 } from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Tag, Typography } from '@cfa/react-components';
import { IconButton } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useTrackResourcePrint } from '@/hooks/useResourcePrintTracking';
import { useTapSentinel } from '@/hooks/useTapSentinel';

import { getCountryLabel } from '@/utils/document-utils/countries';
import { colorTag } from '@/utils/formatDocumentTag';
import { printTridionDocument } from '@/utils/print';
import { cn } from '@/utils/tailwind';

import BreadcrumbLinks from '@/components/BreadcrumbLinks/BreadcrumbLinks';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { Breadcrumb } from '@/types/types';

import { documentAtom, toggleTranslationAtom } from '@/atoms/document';
import { isTridionPreviewAtom } from '@/atoms/is-tridion-preview';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface TridionTitleProps {
  breadcrumbs?: Breadcrumb[];
  title?: string;
  /** Audience tags for a version opened from Choose Version. */
  tags?: string[];
  /** Custom icon content to render */
  iconContent?: React.ReactNode;
}

const TridionTitle: FC<TridionTitleProps> = ({
  breadcrumbs = [],
  title = '',
  tags = [],
  iconContent,
}) => {
  const { chooseDocumentVersion } = useFlags();
  const { t } = useTranslation();
  const trackResourcePrint = useTrackResourcePrint();
  const toggleTranslation = useSetAtom(toggleTranslationAtom);
  const isTridionPreview = useAtomValue(isTridionPreviewAtom);
  const { hasTranslatedValue: showTranslateIcon, chosenDocumentCountry } =
    useAtomValue(documentAtom);
  const { country: userCountry } = useAtomValue(userPrefsAtom);
  const { data: auth } = useAuth();
  const isAdminOperatorOrStakeholder = auth?.isAdminOperatorOrStakeholder;

  const enableChooseDocumentVersion =
    chooseDocumentVersion && isAdminOperatorOrStakeholder;
  const documentCountry = chosenDocumentCountry ?? userCountry.id;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { markFired } = useTapSentinel(wrapperRef, {
    debug: 'Clicking on the print button in DocumentTitle for mobile',
  });

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {t('Generic.linkCopied')}
        </ToastMessageBlock>
      ));
    } catch (error) {
      datadogLogs.logger.warn('clipboard_write_failed', {
        error: error instanceof Error ? error.message : String(error),
        source: 'TridionTitle',
      });
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {`${t('GenericError.error')}: ${t('GenericError.tryAgain')}`}
        </ToastMessageBlock>
      ));
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'p' ||
          event.charCode === 16 ||
          event.charCode === 112 ||
          event.keyCode === 80)
      ) {
        event.stopPropagation();
        event.preventDefault(); // Prevent default print dialog
        // Preview does not update documentAtom — do not track stale metadata.
        if (!isTridionPreview) {
          trackResourcePrint('keyboard', title);
        }
        printTridionDocument();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [trackResourcePrint, title, isTridionPreview]);

  const audienceTags = tags.map(tag => (
    <Tag
      className="md:mr-2.5 md:font-normal"
      color={colorTag(tag)}
      key={tag}
      label={tag}
      variant="filled"
    />
  ));
  const countryTag =
    documentCountry && enableChooseDocumentVersion ? (
      <Tag
        className="md:mr-2.5 md:font-normal"
        color="secondaryBlue"
        key={documentCountry}
        label={getCountryLabel(documentCountry)}
        variant="outlined"
      />
    ) : null;
  const titleTags = (
    <>
      {audienceTags}
      {countryTag}
    </>
  );

  const handlePrintMobile = () => {
    markFired();
    if (!isTridionPreview) {
      trackResourcePrint('button', title);
    }
    printTridionDocument();
  };

  return (
    <div className="shadow-elevation-16 bg-base-0 relative w-full pb-12">
      {breadcrumbs.length > 0 && (
        <div className="px-10 pt-10 pb-0">
          <BreadcrumbLinks breadcrumbs={breadcrumbs} />
        </div>
      )}

      {/** Tags are on top of title on mobile */}
      <div className="flex justify-center gap-4 md:hidden">{titleTags}</div>
      <div className="flex justify-center px-5 py-0 md:justify-start md:px-10">
        <Typography
          variant="h2"
          color="secondary"
          className="dark:text-contrast! mt-3! mb-2!"
        >
          <div style={{ display: 'inline', marginRight: '10px' }}>{title}</div>

          {/** Tags are to the right of title on desktop */}
          <div className="hidden md:inline">{titleTags}</div>
        </Typography>
      </div>
      <div
        className={cn(
          '-mb-10 flex items-center justify-end pr-1 md:hidden md:w-0',
          {
            hidden: isTridionPreview,
          },
        )}
      >
        <IconButton
          className={cn(showTranslateIcon && 'dark:text-contrast!')}
          color="secondary"
          onPressEnd={toggleTranslation}
          isDisabled={!showTranslateIcon}
        >
          <IconLanguage />
        </IconButton>
        <div ref={wrapperRef} className="inline-block">
          <IconButton
            className="dark:text-contrast!"
            color="secondary"
            onPressEnd={() => handlePrintMobile()}
          >
            <IconPrinter />
          </IconButton>
        </div>
        <IconButton
          className="dark:text-contrast!"
          color="secondary"
          onPress={() => {
            void onCopyLink();
          }}
        >
          <IconShare2 />
        </IconButton>
      </div>

      <div
        className={cn(
          'bg-base-0 absolute bottom-0 box-border inline-block p-[3px]',
          'translate-y-[50%] lg:left-10',
          'shadow-elevation-8 rounded-full',
          'left-1/2 -translate-x-1/2 lg:translate-x-0',
        )}
      >
        <div className="border-brand-navy rounded-full border-[3px] border-solid!">
          <div className="flex size-[72px] items-center justify-center">
            {iconContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TridionTitle;
